/**
 * <s-header-search> — Header search bar with live product suggestions.
 *
 * - As the shopper types (2+ characters), fetches matching products from the
 *   storefront predictive search endpoint and lists them under the bar.
 * - WAI-ARIA combobox: arrow keys move through suggestions, Enter opens the
 *   active one (or submits the search), Escape closes the list.
 * - When the field is empty and idle, the placeholder rotates through hints.
 *
 * Data attributes (written by Liquid in snippets/header-search-bar.liquid):
 *   data-suggest-url  — routes.predictive_search_url
 *   data-currency     — ISO currency code for price formatting
 *   data-locale       — locale for price formatting
 *   data-hints        — JSON array of placeholder hints
 *   data-count-label  — announcement template, "[count]" replaced by the number
 *   data-empty-label  — text shown when nothing matches
 */
class HeaderSearch extends HTMLElement {
  #ac;
  #input;
  #panel;
  #list;
  #status;
  #viewAll;
  #debounce;
  #request;
  #hintTimer;
  #activeIndex = -1;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#input = this.querySelector('input[type="search"]');
    this.#panel = this.querySelector('[data-search-panel]');
    this.#list = this.querySelector('[role="listbox"]');
    this.#status = this.querySelector('[data-search-status]');
    this.#viewAll = this.querySelector('[data-search-view-all]');
    if (!this.#input || !this.#panel || !this.#list) return;

    this.#input.addEventListener('input', () => this.#queue(), opts);
    this.#input.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);
    this.#input.addEventListener('focus', () => {
      if (this.#list.children.length && this.#input.value.trim().length >= 2) this.#open();
    }, opts);
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.#close();
    }, opts);
    this.#list.addEventListener('mousedown', (e) => e.preventDefault(), opts);

    this.#startHints();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#request?.abort();
    clearTimeout(this.#debounce);
    clearInterval(this.#hintTimer);
  }

  #startHints() {
    let hints = [];
    try { hints = JSON.parse(this.dataset.hints || '[]'); } catch { hints = []; }
    if (hints.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let index = 0;
    this.#hintTimer = setInterval(() => {
      if (this.#input.value || document.activeElement === this.#input) return;
      index = (index + 1) % hints.length;
      this.#input.classList.add('is-swapping');
      setTimeout(() => {
        this.#input.placeholder = hints[index];
        this.#input.classList.remove('is-swapping');
      }, 200);
    }, 3200);
  }

  #queue() {
    clearTimeout(this.#debounce);
    const term = this.#input.value.trim();
    if (term.length < 2) {
      this.#request?.abort();
      this.#clear();
      this.#close();
      return;
    }
    this.#debounce = setTimeout(() => this.#fetch(term), 200);
  }

  async #fetch(term) {
    this.#request?.abort();
    this.#request = new AbortController();
    const url = new URL(this.dataset.suggestUrl, window.location.origin);
    url.pathname = url.pathname.replace(/\/?$/, '') + '.json';
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      const products = data?.resources?.results?.products ?? [];
      if (this.#input.value.trim() !== term) return;
      this.#render(products, term);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#clear();
        this.#close();
      }
    }
  }

  #formatPrice(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'GBP',
      }).format(amount);
    } catch {
      return String(value);
    }
  }

  #render(products, term) {
    this.#clear();
    const baseId = this.#list.id;

    products.forEach((product, i) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.id = `${baseId}-${i}`;
      li.setAttribute('aria-selected', 'false');
      li.className = 'header-search__option';
      li.dataset.url = product.url;

      const link = document.createElement('a');
      link.href = product.url;
      link.tabIndex = -1;

      if (product.image) {
        const img = document.createElement('img');
        const src = new URL(product.image, window.location.origin);
        src.searchParams.set('width', '120');
        img.src = src.toString();
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        link.append(img);
      } else {
        const blank = document.createElement('span');
        blank.className = 'header-search__thumb';
        blank.setAttribute('aria-hidden', 'true');
        link.append(blank);
      }

      const text = document.createElement('span');
      text.className = 'header-search__text';
      const title = document.createElement('span');
      title.className = 'header-search__title';
      title.textContent = product.title;
      const price = document.createElement('span');
      price.className = 'header-search__price';
      price.textContent = this.#formatPrice(product.price);
      text.append(title, price);
      link.append(text);

      li.append(link);
      this.#list.append(li);
    });

    if (this.#viewAll) {
      const viewUrl = new URL(this.#viewAll.dataset.base, window.location.origin);
      viewUrl.searchParams.set('q', term);
      this.#viewAll.href = viewUrl.pathname + viewUrl.search;
    }

    this.#panel.toggleAttribute('data-empty', products.length === 0);
    if (this.#status) {
      this.#status.textContent = products.length
        ? (this.dataset.countLabel || '').replace('[count]', String(products.length))
        : (this.dataset.emptyLabel || '');
    }
    this.#open();
  }

  #clear() {
    this.#list.replaceChildren();
    this.#activeIndex = -1;
    this.#input.removeAttribute('aria-activedescendant');
  }

  #open() {
    this.#panel.hidden = false;
    this.#input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    this.#panel.hidden = true;
    this.#input.setAttribute('aria-expanded', 'false');
    this.#setActive(-1);
  }

  #setActive(index) {
    const options = [...this.#list.querySelectorAll('[role="option"]')];
    options.forEach((option, i) => option.setAttribute('aria-selected', String(i === index)));
    this.#activeIndex = index;
    if (index >= 0 && options[index]) {
      this.#input.setAttribute('aria-activedescendant', options[index].id);
      options[index].scrollIntoView({ block: 'nearest' });
    } else {
      this.#input.removeAttribute('aria-activedescendant');
    }
  }

  #handleKeydown(event) {
    const options = this.#list.querySelectorAll('[role="option"]');
    if (event.key === 'Escape') {
      if (!this.#panel.hidden) {
        event.preventDefault();
        this.#close();
      }
      return;
    }
    if (!options.length || this.#panel.hidden) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#setActive((this.#activeIndex + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#setActive(this.#activeIndex <= 0 ? options.length - 1 : this.#activeIndex - 1);
    } else if (event.key === 'Enter' && this.#activeIndex >= 0) {
      event.preventDefault();
      window.location.href = options[this.#activeIndex].dataset.url;
    }
  }
}

if (!customElements.get('s-header-search')) {
  customElements.define('s-header-search', HeaderSearch);
}
